
const WhySundaySchoolStillMatters = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[500px] flex items-center justify-center"
        style={{
          backgroundImage: "url('./images/blog-related.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            Why Sunday School Still Matters in Today’s World
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Discover why Sunday School remains a cornerstone of faith, character, and community in our modern age.
          </p>
        </div>
      </div>

      <section className="body--container w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            In a fast-paced, technology-driven world where information is at our fingertips and social values are constantly evolving,
            the idea of Sunday School might seem old-fashioned to some. After all, children today are growing up surrounded by digital
            learning tools, extracurricular activities, and a wide range of educational options. So, does Sunday School still have a place
            in modern society?
          </p>

          <p className="mb-6">The short answer is — yes, it absolutely does.</p>

          <p className="mb-6">
            Sunday School continues to play a vital role in nurturing not only faith but also character, community, and compassion in
            young hearts and minds. Let’s explore why it remains so relevant and important even in today’s world.
          </p>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              1. Building a Strong Moral Foundation
            </h2>
            <p>
              In an age where children are constantly exposed to mixed messages through media, social networks, and peer influence, Sunday
              School provides a safe and consistent moral compass. It helps young learners understand timeless values such as honesty,
              kindness, patience, forgiveness, and respect. Through Bible stories, discussions, and interactive lessons, children learn
              not only what is right but why it is right — equipping them to make ethical decisions throughout life.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              2. Nurturing Faith in a Distracted Generation
            </h2>
            <p>
              With screens competing for attention every second, spiritual growth can easily take a back seat. Sunday School offers a
              dedicated time and space for children — and even adults — to focus on their relationship with God, free from digital noise
              and distractions. It encourages meaningful reflection, prayer, and understanding of Scripture in ways that resonate with
              different age groups.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              3. Building a Sense of Community and Belonging
            </h2>
            <p>
              In a world where loneliness and social isolation are on the rise, Sunday School fosters a sense of belonging and connection.
              It’s more than just a classroom — it’s a community where children form friendships grounded in shared values and mutual
              respect. Teachers and volunteers act as mentors, guiding them through both spiritual and personal growth.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              4. Encouraging Lifelong Learning and Curiosity
            </h2>
            <p>
              Sunday School doesn’t just teach Bible verses — it encourages critical thinking, empathy, and curiosity. Students learn that
              faith isn’t about blind acceptance but thoughtful engagement. This approach nurtures lifelong learners who value wisdom and
              truth, preparing them to live out their faith with confidence and purpose.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              5. Strengthening Families and Intergenerational Bonds
            </h2>
            <p>
              Sunday School is part of a larger faith journey that includes families and the church community. When children share what
              they’ve learned at home, it sparks meaningful conversations that deepen collective faith. Family activities and service
              projects further strengthen bonds between generations through shared experiences rooted in faith.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              6. Teaching Compassion Through Service
            </h2>
            <p>
              One of the most powerful lessons Sunday School imparts is the importance of serving others. Whether through mission
              activities, charity drives, or acts of kindness, children learn that their faith calls them to make a difference in the
              world. This helps counter the self-centered culture of modern society and instills empathy and a heart for service.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              7. A Safe Space for Emotional and Spiritual Growth
            </h2>
            <p>
              In times of uncertainty, children and teens need safe spaces to express their fears, doubts, and hopes. Sunday School
              provides that nurturing environment — a place where they are seen, heard, and loved by their community and by God. This
              support builds confidence and resilience that lasts a lifetime.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              8. Adapting to Modern Times
            </h2>
            <p>
              The beauty of Sunday School today lies in its ability to evolve without losing its purpose. Many programs now use multimedia
              tools, creative storytelling, and interactive sessions to keep lessons engaging. Online Sunday Schools, podcasts, and digital
              Bible games have made faith education accessible and exciting for the modern generation.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              Conclusion: Timeless Lessons for a Changing World
            </h2>
            <p>
              While society continues to evolve, the core needs of the human heart remain — love, purpose, and belonging. Sunday School
              meets these needs by blending spiritual wisdom with real-world application. It reminds us that faith is not about rituals but
              about living with integrity, compassion, and hope in a rapidly changing world.
            </p>
            <p className="mt-4 font-medium">
              So yes — Sunday School still matters. In fact, in today’s complex and fast-changing world, it might matter more than ever before.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-6">
          <div className="flex flex-wrap gap-2">
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Sunday School</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Faith Education</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Christian Community</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Spiritual Growth</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhySundaySchoolStillMatters;